/**
 * Billing API contract — frontend.
 *
 * MIRROR: keep in sync with functions/contracts/billingApi.js.
 * Any change to a status value, feature key, or helper MUST update both copies
 * in the same commit.
 *
 * Executes docs/phase-1/LANE-B-BILLING.md with two deliberate deviations,
 * documented here so neither is mistaken for drift:
 *
 *   1. `trialing` is a DISTINCT status, not collapsed into `active`.
 *      LANE-B line 225 maps `active|trialing -> active`. We keep them apart
 *      because the UI renders "TRIAL · N DAYS LEFT", the trial-ending email
 *      targets exactly this status, and trial->paid conversion is the number
 *      the whole gate is built to move. Collapsing it would erase all three.
 *
 *      Nothing outside this file compares a status string. Everything gates on
 *      isPro() / hasFeature(), so adding a future status is a one-file change.
 *
 *   2. `features` carries six keys, not two. The gate now spans the routine,
 *      the scan unlock, the render, and the newsletter — not just vault +
 *      glowup.
 *
 * `scan_unlock` is the feature that bypasses the judging gate. Note it is NOT
 * named `reveal`: COPY.REVEAL_* and ScanRevealModal already own that word for
 * the theatrical score animation, which every user sees once their scan opens
 * — by either path.
 *
 * @typedef {("active"|"trialing"|"past_due"|"canceled"|"incomplete"|"none")} EntitlementStatus
 * @typedef {("vault"|"glowup"|"routine"|"scan_unlock"|"glowup_render"|"newsletter")} FeatureKey
 *
 * @typedef {Object} EntitlementDoc
 * @property {string} user_id
 * @property {EntitlementStatus} status
 * @property {Object<FeatureKey, boolean>} features
 * @property {string|null} stripe_customer_id
 * @property {string|null} stripe_subscription_id
 * @property {*} current_period_end      // Firestore Timestamp | null
 * @property {*} trial_end               // Firestore Timestamp | null
 * @property {boolean} has_used_trial
 * @property {boolean} cancel_at_period_end
 * @property {*} updated_at
 * @property {"webhook"|"system"} updated_by
 *
 * @typedef {Object} CheckoutSessionResponse
 * @property {"success"} status
 * @property {string} checkout_url
 *
 * @typedef {Object} PortalSessionResponse
 * @property {"success"} status
 * @property {string} portal_url
 *
 * @typedef {Object} BillingErrorResponse
 * @property {"error"} status
 * @property {BillingErrorCode} error_code
 * @property {string} error_message
 *
 * @typedef {("UNAUTHENTICATED"|"STRIPE_API"|"ALREADY_SUBSCRIBED"
 *   |"NOT_SUBSCRIBED"|"INTERNAL")} BillingErrorCode
 */

/**
 * Free-trial length in days, passed to Stripe as
 * subscription_data.trial_period_days.
 *
 * A CONSTANT, deliberately — not an env var, not a repo variable. This number
 * is quoted verbatim in COPY.UNLOCK_GATE_TRIAL_TERMS ("7 days free, then
 * $4.99/mo"), and a value living outside the repo cannot be checked against
 * the copy by any test. That is exactly how the modal came to advertise $11.99
 * against a $9.99 charge; priceAgreement.test.js now binds both numbers to
 * their source, and it can only do that for values the repo can see.
 *
 * The flexibility an env var would buy is imaginary anyway: changing the trial
 * means changing the sentence that states it, so it ships as a commit either
 * way. Unlike STRIPE_PRICE_ID_MONTHLY — a Stripe object reference that really
 * does differ between test and live mode — this is the same integer in every
 * environment.
 *
 * 0 means no trial. Stripe REJECTS trial_period_days: 0, so callers must omit
 * the field rather than pass a zero (see createCheckoutSession).
 */
export const TRIAL_DAYS = 7;

export const ENTITLEMENT_STATUS = Object.freeze({
  ACTIVE: 'active',
  TRIALING: 'trialing',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  INCOMPLETE: 'incomplete',
  NONE: 'none',
});

/**
 * Every gated surface in the product. Adding a key here means adding a
 * requireEntitlement() call site on the backend — a feature nothing enforces
 * is worse than no feature at all.
 */
export const FEATURES = Object.freeze({
  VAULT: 'vault',
  GLOWUP: 'glowup',
  ROUTINE: 'routine',
  SCAN_UNLOCK: 'scan_unlock',
  GLOWUP_RENDER: 'glowup_render',
  NEWSLETTER: 'newsletter',
});

export const FEATURE_KEYS = Object.freeze(Object.values(FEATURES));

/** Features granted the moment a subscription (or its trial) starts. */
export const PRO_FEATURES = Object.freeze({
  vault: true,
  glowup: true,
  routine: true,
  scan_unlock: true,
  glowup_render: true,
  newsletter: true,
});

/** Features on a lapsed subscription. Also the shape written on cancel. */
export const NO_FEATURES = Object.freeze({
  vault: false,
  glowup: false,
  routine: false,
  scan_unlock: false,
  glowup_render: false,
  newsletter: false,
});

export const BILLING_ERROR_CODES = Object.freeze({
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  STRIPE_API: 'STRIPE_API',
  ALREADY_SUBSCRIBED: 'ALREADY_SUBSCRIBED',
  NOT_SUBSCRIBED: 'NOT_SUBSCRIBED',
  INTERNAL: 'INTERNAL',
});

/**
 * The single paying-customer predicate. NOTHING outside this file may compare
 * `status` directly — a call site that writes `status === "active"` silently
 * locks every trialing user out, which is the exact population the gate exists
 * to convert.
 *
 * `past_due` is deliberately NOT pro: Stripe owns the retry window, but the
 * product should not keep serving paid features indefinitely on a dead card.
 * Features are not cleared on past_due (see stripeWebhook) so the user recovers
 * instantly on a successful retry — this predicate is what actually gates.
 *
 * @param {EntitlementDoc|null|undefined} entitlement
 * @returns {boolean}
 */
/**
 * FRONTEND-ONLY, deliberately — not mirrored into functions/contracts/.
 *
 * This interprets a callable RESPONSE, and only a client ever holds one. The
 * server produces `ENTITLEMENT_REQUIRED`; it never consumes it. A mirrored copy
 * would be dead code on the backend, and the mirror rule exists to keep shared
 * VALUES in step, not to duplicate everything living in the file.
 *
 * Did a callable refuse this because the caller is not a member?
 *
 * Every entitlement-gated callable answers with the SAME soft error shape
 * (`ENTITLEMENT_REQUIRED`), so every caller needs the same question answered.
 * Without this the string gets retyped at each render site and one of them
 * eventually falls through to the generic "something went wrong" branch — which
 * is what happened on the Routine page: a paywall was being shown to users as a
 * network error, complete with a retry button that could never succeed.
 *
 * This is the SERVER's answer, and it outranks the client entitlement doc. A
 * stale `entitlements/{uid}` snapshot cannot show a paywall to someone who has
 * already paid past it, nor hide one from someone who has not.
 *
 * @param {{status?: string, error_code?: string}|null|undefined} data a callable response
 * @returns {boolean}
 */
export function isPaywalled(data) {
  return Boolean(data) && data.error_code === 'ENTITLEMENT_REQUIRED';
}

export function isPro(entitlement) {
  if (!entitlement || typeof entitlement !== 'object') return false;
  return (
    entitlement.status === ENTITLEMENT_STATUS.ACTIVE
    || entitlement.status === ENTITLEMENT_STATUS.TRIALING
  );
}

/**
 * @param {EntitlementDoc|null|undefined} entitlement
 * @param {FeatureKey} feature
 * @returns {boolean}
 */
export function hasFeature(entitlement, feature) {
  if (!isPro(entitlement)) return false;
  return entitlement.features?.[feature] === true;
}

/**
 * Normalise the several shapes a timestamp arrives in — Firestore Timestamp
 * (client SDK and Admin SDK both expose toMillis), a Date, an epoch-ms number,
 * or an ISO string from a JSON round-trip in tests.
 *
 * @param {*} value
 * @returns {number|null} epoch ms
 */
export function toMillis(value) {
  if (value == null) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  // Admin SDK Timestamp survives a structured clone as {_seconds, _nanoseconds}.
  if (typeof value._seconds === 'number') return value._seconds * 1000;
  return null;
}

/**
 * Whole days remaining in the trial, or null when not trialing. Rounds UP:
 * a trial with 4 hours left reads "1 DAY LEFT", never "0 DAYS LEFT" on an
 * account that still works.
 *
 * @param {EntitlementDoc|null|undefined} entitlement
 * @param {number} [nowMs] injectable for tests
 * @returns {number|null}
 */
export function trialDaysLeft(entitlement, nowMs) {
  if (!entitlement || entitlement.status !== ENTITLEMENT_STATUS.TRIALING) return null;
  const endMs = toMillis(entitlement.trial_end);
  if (endMs == null) return null;
  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  const remaining = endMs - now;
  if (remaining <= 0) return 0;
  return Math.ceil(remaining / 86400000);
}

/** @param {unknown} obj @returns {obj is CheckoutSessionResponse} */
export function isCheckoutSessionResponse(obj) {
  return Boolean(
    obj && typeof obj === 'object'
      && obj.status === 'success'
      && typeof obj.checkout_url === 'string'
      && obj.checkout_url,
  );
}

/** @param {unknown} obj @returns {obj is PortalSessionResponse} */
export function isPortalSessionResponse(obj) {
  return Boolean(
    obj && typeof obj === 'object'
      && obj.status === 'success'
      && typeof obj.portal_url === 'string'
      && obj.portal_url,
  );
}

/** @param {unknown} obj @returns {obj is BillingErrorResponse} */
export function isBillingErrorResponse(obj) {
  return Boolean(
    obj && typeof obj === 'object'
      && obj.status === 'error'
      && BILLING_ERROR_CODES[obj.error_code],
  );
}


// ── Why a member is leaving ─────────────────────────────────────────────────
//
// Asked BEFORE the confirm, not after. A reason collected after the cancel has
// already gone through is answered by people being polite on their way out;
// asked before, it is answered by people deciding.
//
// The list is deliberately short and blunt. Long reason lists get the top item
// picked regardless, and a "please tell us more" essay box gets skipped. One
// tap plus an optional line is the most honest thing that will actually be
// filled in.
//
// OTHER is last and always available — a forced-choice list with no escape
// hatch produces a plausible-looking answer rather than a true one.
//
// Never blocks the cancellation. A member who wants out and gets a validation
// error on their way is a chargeback, and the reason is worth less than the
// goodwill. The callable accepts a missing reason.
export const CANCEL_REASONS = Object.freeze([
  { key: 'too_expensive', label: 'Too expensive' },
  { key: 'not_using', label: "I'm not using it enough" },
  { key: 'got_what_i_needed', label: 'I got what I needed' },
  { key: 'missing_features', label: 'Missing something I wanted' },
  { key: 'technical_issues', label: 'It did not work properly' },
  { key: 'other', label: 'Something else' },
]);

export const CANCEL_REASON_KEYS = Object.freeze(CANCEL_REASONS.map((r) => r.key));

/** Free-text cap. Long enough for a real sentence, short enough to store. */
export const CANCEL_NOTE_MAX = 300;

/** @param {unknown} v @returns {boolean} */
export function isCancelReason(v) {
  return typeof v === 'string' && CANCEL_REASON_KEYS.includes(v);
}

/**
 * Sample fixture for tests and component development.
 * @type {EntitlementDoc}
 */
export const SAMPLE_ENTITLEMENT = Object.freeze({
  user_id: 'sample-uid',
  status: ENTITLEMENT_STATUS.TRIALING,
  features: { ...PRO_FEATURES },
  stripe_customer_id: 'cus_sample',
  stripe_subscription_id: 'sub_sample',
  current_period_end: null,
  trial_end: null,
  has_used_trial: true,
  cancel_at_period_end: false,
  updated_at: null,
  updated_by: 'webhook',
});
